import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";

export const DEFAULT_WIN_COLOR = "#68B984"; // Green matching the pattern
export const DEFAULT_LOSS_COLOR = "#F4D03F"; // Yellow matching the pattern

const CHART_CONSTANTS = {
  AXIS: { MIN_GRID_DISTANCE: 30 },
  DEFAULT_LABEL_MAX_WIDTH: 120,
  MIN_LABEL_WIDTH: 70,
  LABEL_PADDING: 2,
};

const handleAxisResize = (axis: am4charts.Axis) => {
  if (
    !axis.pixelWidth ||
    axis.endIndex === undefined ||
    axis.startIndex === undefined
  ) {
    return;
  }

  // Calculate available width per column
  const totalColumns = axis.endIndex - axis.startIndex + 1;
  const availableWidth = axis.pixelWidth;
  const widthPerColumn = availableWidth / totalColumns;

  const labelPadding = CHART_CONSTANTS.LABEL_PADDING;
  const maxLabelWidth = Math.max(
    widthPerColumn - labelPadding,
    CHART_CONSTANTS.MIN_LABEL_WIDTH
  );

  axis.renderer.labels.template.maxWidth = maxLabelWidth;
  axis.renderer.labels.template.truncate = true;
  axis.renderer.labels.template.wrap = false;

  axis.renderer.labels.template.paddingLeft = CHART_CONSTANTS.LABEL_PADDING;
  axis.renderer.labels.template.paddingRight = CHART_CONSTANTS.LABEL_PADDING;

  // Force labels to be redrawn
  axis.invalidateLabels();
};

export function configureWinLossChartPadding(chart: am4charts.XYChart) {
  chart.paddingTop = 20;
  chart.paddingRight = 20;
  chart.paddingBottom = 40;
  chart.paddingLeft = 20;
}

export function createWinLossCategoryAxis(chart: am4charts.XYChart) {
  const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
  categoryAxis.dataFields.category = "category";
  categoryAxis.renderer.grid.template.location = 0;
  categoryAxis.renderer.minGridDistance =
    CHART_CONSTANTS.AXIS.MIN_GRID_DISTANCE;

  // Apply dynamic label resizing logic like distribution charts
  categoryAxis.renderer.labels.template.maxWidth =
    CHART_CONSTANTS.DEFAULT_LABEL_MAX_WIDTH;
  categoryAxis.renderer.labels.template.truncate = true;
  categoryAxis.renderer.labels.template.wrap = false;
  categoryAxis.renderer.labels.template.textAlign = "middle";
  categoryAxis.renderer.labels.template.tooltipText = "{category}";

  // Add dynamic resizing event handlers
  categoryAxis.events.on("sizechanged", function (ev) {
    const axis = ev.target;
    handleAxisResize(axis);
  });

  chart.events.on("datavalidated", function () {
    handleAxisResize(categoryAxis);
  });

  categoryAxis.renderer.labels.template.paddingLeft = 5;
  categoryAxis.renderer.labels.template.paddingRight = 5;

  return categoryAxis;
}

export function createWinLossPercentageAxis(chart: am4charts.XYChart) {
  const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
  valueAxis.min = 0;
  valueAxis.max = 100;
  valueAxis.strictMinMax = true;
  valueAxis.title.text = "Win/Loss";
  return valueAxis;
}

export function createWinSeries(
  chart: am4charts.XYChart,
  winColor: string = DEFAULT_WIN_COLOR
) {
  const winSeries = chart.series.push(new am4charts.ColumnSeries());
  winSeries.dataFields.valueY = "winPercentage";
  winSeries.dataFields.categoryX = "category";
  winSeries.name = "Win";
  winSeries.stacked = true;
  winSeries.fill = am4core.color(winColor);
  winSeries.stroke = am4core.color(winColor);
  winSeries.strokeWidth = 1;
  winSeries.columns.template.tooltipText =
    "[bold]{name}[/]\n{categoryX}: {winCount} {winCountText} ({winPercentage})";

  // Add striped pattern to win series columns (matching ICP distribution chart pattern)
  winSeries.columns.template.adapter.add("fill", () => {
    const mainColor = am4core.color(winColor);

    // Apply striped pattern to all win columns with reduced contrast
    const pattern = new am4core.LinePattern();
    pattern.strokeWidth = 3;
    pattern.gap = 15;
    const size = 20;
    pattern.width = size + pattern.gap;
    pattern.height = size + pattern.gap;
    pattern.rotation = -25;
    pattern.stroke = mainColor.brighten(0.3);
    pattern.strokeOpacity = 0.3;
    pattern.backgroundFill = mainColor;
    pattern.backgroundOpacity = 1;
    return pattern;
  });

  // Add labels to win series
  const winLabelBullet = winSeries.bullets.push(new am4charts.LabelBullet());
  winLabelBullet.label.text = "{winPercentage}";
  winLabelBullet.label.fill = am4core.color("#ffffff"); // White text on green background
  winLabelBullet.label.fontSize = 12;
  winLabelBullet.label.fontWeight = "bold";
  winLabelBullet.locationY = 0.5;
  winLabelBullet.label.hideOversized = true;
  winLabelBullet.label.interactionsEnabled = false;

  return winSeries;
}

export function createLossSeries(
  chart: am4charts.XYChart,
  lossColor: string = DEFAULT_LOSS_COLOR
) {
  const lossSeries = chart.series.push(new am4charts.ColumnSeries());
  lossSeries.dataFields.valueY = "lossPercentage";
  lossSeries.dataFields.categoryX = "category";
  lossSeries.name = "Loss";
  lossSeries.stacked = true;
  lossSeries.fill = am4core.color(lossColor);
  lossSeries.stroke = am4core.color(lossColor);
  lossSeries.strokeWidth = 1;
  lossSeries.columns.template.tooltipText =
    "[bold]{name}[/]\n{categoryX}: {lossCount} {lossCountText} ({lossPercentage})";

  // Add labels to loss series
  const lossLabelBullet = lossSeries.bullets.push(new am4charts.LabelBullet());
  lossLabelBullet.label.text = "{lossPercentage}";
  lossLabelBullet.label.fill = am4core.color("#333333"); // Dark text on yellow background
  lossLabelBullet.label.fontSize = 12;
  lossLabelBullet.label.fontWeight = "bold";
  lossLabelBullet.locationY = 0.5;
  lossLabelBullet.label.hideOversized = true;
  lossLabelBullet.label.interactionsEnabled = false;

  return lossSeries;
}

export function createWinLossLegend(
  chart: am4charts.XYChart,
  position: "top" | "bottom" | "left" | "right" = "bottom"
) {
  chart.legend = new am4charts.Legend();
  chart.legend.position = position;
  chart.legend.contentAlign = "right";
  chart.legend.paddingBottom = 20;
  chart.legend.labels.template.maxWidth = 95;
  return chart.legend;
}
